
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { useGym } from "@/contexts/gym-context"
import { GymSelector } from "@/components/financial-reports/gym-selector"
import { DateRangePicker } from "@/components/financial-reports/date-range-picker"

export default function FinancialReportsPage() {
  const { t } = useTranslation()
  const { selectedGym } = useGym()
  const [dateRange, setDateRange] = useState<{ from: Date; to: Date }>({
    from: new Date(new Date().setMonth(new Date().getMonth() - 1)),
    to: new Date(),
  })

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-2">
        <h1 className="text-3xl font-bold tracking-tight">{t("financialReports.title")}</h1>
        <p className="text-muted-foreground">{t("financialReports.description")}</p>
      </div>

      <div className="flex flex-col sm:flex-row gap-4 justify-between">
        <GymSelector />
        <DateRangePicker
          dateRange={dateRange}
          setDateRange={(range) => {
            if (range?.from && range?.to) setDateRange({ from: range.from, to: range.to });
          }}
        />
      </div>

      {/* Placeholder for FinancialOverview */}
      <Card>
        <CardHeader>
          <CardTitle>{t("financialReports.financialOverview")}</CardTitle>
          <CardDescription>{t("financialReports.financialOverviewDescription")}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="p-4 border rounded-lg">
              <div className="text-sm text-muted-foreground">{t("financialReports.totalRevenue")}</div>
              <div className="text-2xl font-bold">$0.00</div>
            </div>
            <div className="p-4 border rounded-lg">
              <div className="text-sm text-muted-foreground">{t("financialReports.totalExpenses")}</div>
              <div className="text-2xl font-bold">$0.00</div>
            </div>
            <div className="p-4 border rounded-lg">
              <div className="text-sm text-muted-foreground">{t("financialReports.netProfit")}</div>
              <div className="text-2xl font-bold">$0.00</div>
            </div>
            <div className="p-4 border rounded-lg">
              <div className="text-sm text-muted-foreground">{t("financialReports.activeSubscriptions")}</div>
              <div className="text-2xl font-bold">0</div>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>{t("financialReports.revenueOverTime")}</CardTitle>
            <CardDescription>{t("financialReports.revenueOverTimeDescription")}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[300px] flex items-center justify-center text-muted-foreground">
              {t("financialReports.chartPlaceholder")}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t("financialReports.expenseBreakdown")}</CardTitle>
            <CardDescription>{t("financialReports.expenseBreakdownDescription")}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[300px] flex items-center justify-center text-muted-foreground">
              {t("financialReports.chartPlaceholder")}
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="subscriptions">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="subscriptions">{t("financialReports.subscriptionRevenue")}</TabsTrigger>
          <TabsTrigger value="payments">{t("financialReports.paymentHistory")}</TabsTrigger>
        </TabsList>
        <TabsContent value="subscriptions">
          <Card>
            <CardHeader>
              <CardTitle>{t("financialReports.subscriptionRevenue")}</CardTitle>
              <CardDescription>{t("financialReports.subscriptionRevenueDescription")}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                {t("financialReports.chartPlaceholder")}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        <TabsContent value="payments">
          <Card>
            <CardHeader>
              <CardTitle>{t("financialReports.paymentHistory")}</CardTitle>
              <CardDescription>{t("financialReports.paymentHistoryDescription")}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                {t("financialReports.chartPlaceholder")}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  )
}
